import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { motion } from "framer-motion"
import { TFunction } from "i18next"
import { formatNumber } from "lopermut"
import { ConditionBadge } from "modules/workflows/components/Builder/nodes/ConditionNode"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CampaignLogKindEnum,
  CampaignLogResponseDataSchema,
  ConditionsNotMetLogSchema,
  FilterOutLogSchema,
  FilterOutReason,
  TriggerDelayedLogSchema,
  WorkflowConditionFieldEnum,
} from "schema"
import z from "zod"

interface Props {
  data: z.infer<typeof CampaignLogResponseDataSchema>
}

type LoggedCondition = z.infer<typeof ConditionsNotMetLogSchema>["metConditions"][number]

function formatDelayInHours(amount: number, t: TFunction) {
  return `${formatNumber({ amount, fix: 2 })} ${t("campaigns.detail.logs.hour", { count: amount })}`
}

function buildFilterOutReasonCopyMap(t: TFunction) {
  return {
    [FilterOutReason.notLaunched]: t("campaigns.detail.logs.filterOutReasons.notLaunched"),
    [FilterOutReason.notActive]: t("campaigns.detail.logs.filterOutReasons.notActive"),
    [FilterOutReason.finished]: t("campaigns.detail.logs.filterOutReasons.finished"),
    [FilterOutReason.archived]: t("campaigns.detail.logs.filterOutReasons.archived"),
    [FilterOutReason.optedOut]: t("campaigns.detail.logs.filterOutReasons.optedOut"),
    [FilterOutReason.emailBounced]: t("campaigns.detail.logs.filterOutReasons.emailBounced"),
    [FilterOutReason.emailRejected]: t("campaigns.detail.logs.filterOutReasons.emailRejected"),
    [FilterOutReason.ongoingConversation]: t(
      "campaigns.detail.logs.filterOutReasons.ongoingConversation",
    ),
    [FilterOutReason.noWhatsappNumber]: t(
      "campaigns.detail.logs.filterOutReasons.noWhatsappNumber",
    ),
    [FilterOutReason.noWhatsappTemplates]: t(
      "campaigns.detail.logs.filterOutReasons.noWhatsappTemplates",
    ),
    [FilterOutReason.noEmail]: t("campaigns.detail.logs.filterOutReasons.noEmail"),
    [FilterOutReason.noEmailTemplates]: t(
      "campaigns.detail.logs.filterOutReasons.noEmailTemplates",
    ),
    [FilterOutReason.noChannel]: t("campaigns.detail.logs.filterOutReasons.noChannel"),
    [FilterOutReason.noCredits]: t("campaigns.detail.logs.filterOutReasons.noCredits"),
    [FilterOutReason.goalFrequency]: t("campaigns.detail.logs.filterOutReasons.goalFrequency"),
    [FilterOutReason.noEligibleGoals]: t("campaigns.detail.logs.filterOutReasons.noEligibleGoals"),
    [FilterOutReason.recentConversation]: t(
      "campaigns.detail.logs.filterOutReasons.recentConversation",
    ),
  } satisfies Record<FilterOutReason, string>
}

export const ExpandedRow = ({ data }: Props) => {
  const { t } = useTranslation()

  const renderCondition = useCallback(
    (condition: LoggedCondition, index: number) => {
      if (condition.field === WorkflowConditionFieldEnum.LLMPrompt) {
        const prompt =
          condition.config &&
          typeof condition.config === "object" &&
          "prompt" in condition.config &&
          typeof condition.config.prompt === "string"
            ? condition.config.prompt
            : null

        return (
          <li
            key={index}
            className="flex flex-col gap-1 pl-2 ml-2 relative before:content-['-'] before:absolute before:left-[-0.5em] before:top-0"
          >
            <div className="flex items-center gap-1 flex-wrap">
              <ConditionBadge empty={false}>
                {t("campaigns.condition.fields.LLMPrompt")}
              </ConditionBadge>
              <ConditionBadge isOperator empty={false}>
                {t(`campaigns.condition.operators.${condition.operator}`)}
              </ConditionBadge>
            </div>
            {prompt && (
              <span className="text-sm text-muted-foreground whitespace-pre-wrap">{prompt}</span>
            )}
          </li>
        )
      }

      return (
        <li
          key={index}
          className="flex items-center gap-1 pl-2 ml-2 relative before:content-['-'] before:absolute before:left-[-0.5em] before:top-0"
        >
          <ConditionBadge empty={!condition.field}>{condition.field}</ConditionBadge>
          <ConditionBadge isOperator empty={!condition.operator}>
            {condition.operator}
          </ConditionBadge>
          {condition.value && (
            <ConditionBadge empty={!condition.value}>{condition.value}</ConditionBadge>
          )}
        </li>
      )
    },
    [t],
  )

  const renderMarkdown = useCallback(() => {
    return (
      <MarkdownRenderer>{`\`\`\`json\n${JSON.stringify(data.log, null, 2)}\n\`\`\``}</MarkdownRenderer>
    )
  }, [data.log])

  const logContent = useMemo(() => {
    switch (data.kind) {
      case CampaignLogKindEnum.Launch:
        return renderMarkdown()

      case CampaignLogKindEnum.FilterOut:
        const filterOutLog = FilterOutLogSchema.safeParse(data.log)
        if (!filterOutLog.success) return renderMarkdown()

        const filterOutReasonCopyMap = buildFilterOutReasonCopyMap(t)
        return (
          <span>
            {t("campaigns.detail.logs.filterOut")}
            <ul className="list-disc list-inside">
              {filterOutLog.data.reasons.map((reason) => (
                <li key={reason}>{filterOutReasonCopyMap[reason as FilterOutReason]}</li>
              ))}
            </ul>
          </span>
        )

      case CampaignLogKindEnum.TriggerDelayed:
        const triggerDelayedLog = TriggerDelayedLogSchema.safeParse(data.log)
        if (!triggerDelayedLog.success) return renderMarkdown()
        return (
          <span>
            {t("campaigns.detail.logs.triggerDelayed", {
              reason: triggerDelayedLog.data.reason,
              originalDelayInHours: formatDelayInHours(
                triggerDelayedLog.data.originalDelayInHours ?? 0,
                t,
              ),
              newDelayInHours: formatDelayInHours(triggerDelayedLog.data.newDelayInHours ?? 0, t),
            })}
          </span>
        )

      case CampaignLogKindEnum.ConditionsNotMet:
        const conditionsNotMetLog = ConditionsNotMetLogSchema.safeParse(data.log)
        if (!conditionsNotMetLog.success) return renderMarkdown()
        return (
          <div className="flex flex-col gap-1">
            {conditionsNotMetLog.data.metConditions.length > 0 && (
              <div className="flex flex-col gap-0">
                <span>{t("campaigns.detail.logs.conditions.met")}</span>
                <ul className="list-disc list-inside">
                  {conditionsNotMetLog.data.metConditions.map(renderCondition)}
                </ul>
              </div>
            )}
            {conditionsNotMetLog.data.notMetConditions.length > 0 && (
              <div className="flex flex-col gap-0">
                <span>{t("campaigns.detail.logs.conditions.notMet")}</span>
                <ul className="list-disc list-inside">
                  {conditionsNotMetLog.data.notMetConditions.map(renderCondition)}
                </ul>
              </div>
            )}
          </div>
        )

      case CampaignLogKindEnum.WorkflowActionFailed:
      case CampaignLogKindEnum.WorkflowExecutionSucceeded:
      case CampaignLogKindEnum.WorkflowExecutionFailed:
      case CampaignLogKindEnum.ConversationAchieved:
      case CampaignLogKindEnum.Trigger:
      case CampaignLogKindEnum.TriggerResumed:
      case CampaignLogKindEnum.Outreach:
        return renderMarkdown()
    }
  }, [data.kind, data.log, renderCondition, renderMarkdown, t])

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="px-4 py-3 pl-18 bg-muted/30 border-b-2 border-border/50 flex flex-col gap-2">
        {logContent}
      </div>
    </motion.div>
  )
}
